import * as React from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';

import { Button } from '@/componentes/ui/button';
import { useProgresso } from '@/dados';
import { Wrap } from '@/layout/wrap';
import { Admin } from '@/telas/admin';
import { EmpresaDoPainel } from '@/telas/admin/empresa';
import { Custos } from '@/telas/custos';
import { Despesas } from '@/telas/despesas';
import { Empresa } from '@/telas/empresa';
import { Entrar } from '@/telas/entrar';
import { Historico } from '@/telas/historico';
import { Indicadores } from '@/telas/indicadores';
import { Preco } from '@/telas/preco';
import { Projeto } from '@/telas/projeto';
import { Relatorio } from '@/telas/relatorio';
import { Sazonalidade } from '@/telas/sazonalidade';

// A vitrine do design system só existe em desenvolvimento. O ternário sobre
// import.meta.env.DEV é resolvido na compilação: em produção o import dinâmico
// vira código morto e o chunk sequer é gerado.
const Vitrine = import.meta.env.DEV
  ? React.lazy(() => import('@/design/vitrine').then((m) => ({ default: m.Vitrine })))
  : null;

// A peça de referência da direção visual escolhida ("Colmeia viva"), com dados
// fixos. As duas concorrentes foram removidas junto com o alternador quando a
// escolha foi feita. Esta sobrevive só em desenvolvimento, como padrão-ouro
// para comparar quando alguém desconfiar de que uma tela saiu da linguagem.
const Referencia = import.meta.env.DEV
  ? React.lazy(() => import('@/design/direcoes/a-colmeia'))
  : null;

/* Rotas do produto.
 *
 * O casco e a largura da coluna são responsabilidade da TELA (via Pagina),
 * não da rota: cada tela sabe se é etapa estreita do funil, painel ou
 * documento. Aqui só mora o mapa endereço → tela. */
export function Rotas() {
  return (
    <Routes>
      <Route path="/entrar" element={<Entrar />} />

      <Route path="/" element={<Inicio />} />
      <Route path="/empresa" element={<Empresa />} />
      <Route path="/custos" element={<Custos />} />
      <Route path="/despesas" element={<Despesas />} />
      <Route path="/sazonalidade" element={<Sazonalidade />} />
      <Route path="/indicadores" element={<Indicadores />} />
      <Route path="/projeto" element={<Projeto />} />
      <Route path="/relatorio" element={<Relatorio />} />
      <Route path="/historico" element={<Historico />} />

      {/* Fora do funil: a própria tela recusa quem não é admin, e a API
          responde 404 em toda rota do painel para quem não tem o papel. */}
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/empresas/:id" element={<EmpresaDoPainel />} />

      {Vitrine ? (
        <Route
          path="/design"
          element={
            <React.Suspense fallback={<p className="p-8">Carregando vitrine…</p>}>
              <Vitrine />
            </React.Suspense>
          }
        />
      ) : null}

      {Referencia ? (
        <Route
          path="/referencia"
          element={
            <React.Suspense fallback={<p className="p-8">Carregando referência…</p>}>
              <Referencia />
            </React.Suspense>
          }
        />
      ) : null}

      <Route path="*" element={<NaoEncontrada />} />
    </Routes>
  );
}

/* A raiz decide onde o usuário começa: quem já resolveu o funil inteiro cai
 * direto no preço, que é o trabalho do dia; quem ainda deve alguma etapa é
 * levado à primeira pendente (usuário novo cai em Empresa). Enquanto o
 * progresso carrega não se redireciona nada — redirecionar no escuro mandaria
 * todo usuário configurado para /empresa por um instante a cada visita. */
function Inicio() {
  const { progresso, carregando } = useProgresso();
  if (carregando) return null;
  const pendente = progresso.pendente;
  if (pendente && pendente.rota !== '/') return <Navigate to={pendente.rota} replace />;
  return <Preco />;
}

function NaoEncontrada() {
  return (
    <Wrap>
      <div className="grid gap-3 py-12 text-center">
        <h1 className="text-titulo">Página não encontrada</h1>
        <p className="text-muted-foreground text-apoio">
          O endereço acessado não existe nesta ferramenta.
        </p>
        <div>
          <Button asChild>
            <Link to="/">Voltar ao preço</Link>
          </Button>
        </div>
      </div>
    </Wrap>
  );
}
